import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { LoadingProgress } from '../../../../layout';

type PaymentFormSetupProps = {
  mode: 'setup';
  refetchPaymentMethod: () => void;
  showSaveBtn: (show: boolean) => void;
};

type PaymentFormPaymentProps = {
  mode: 'payment';
  onSuccess?: () => void;
  onError?: (error: string) => void;
};

type PaymentFormProps = PaymentFormSetupProps | PaymentFormPaymentProps;

const useStyles = makeStyles(theme => ({
  cardHolderNameLabel: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    display: 'block',
    color: '#30313d',
  },
}));

const PaymentForm = forwardRef<
{ handleSubmit:() => void },
PaymentFormProps
>((
    props,
    ref,
  ) => {
    const stripe = useStripe();
    const elements = useElements();
    const { enqueueSnackbar } = useSnackbar();
    const classes = useStyles();

    const [isStripeLoading, setIsStripeLoading] = useState(true);
    const [paymentReady, setPaymentReady] = useState(false);
    const [cardholderName, setCardholderName] = useState('');
    const [isUsingExistingCard, setIsUsingExistingCard] = useState(false);
    const [nameError, setNameError] = useState('');

    const isSetupMode = props.mode === 'setup';

    useEffect(() => {
      if (elements) {
        const paymentElement = elements.getElement('payment');

        const handleReady = () => {
          setPaymentReady(true);
        };

        const handleChange = (event: StripePaymentElementChangeEvent) => {
        // Handle cardholder name auto-population for both modes
        // Detect new card entry (for both modes)
          if (event.value?.type) {
            setIsUsingExistingCard(false);
            // Setup mode specific logic
            if (isSetupMode) {
              props.showSaveBtn(true);
            }
          }

          // Check if switching to saved payment method
          if (event.value?.payment_method) {
            setIsUsingExistingCard(true);
            // Auto-populate cardholder name from saved payment method
            const savedName = event.value.payment_method.billing_details?.name;
            if (savedName) {
              setCardholderName(savedName);
              setNameError('');
            }
            // Setup mode specific logic
            if (isSetupMode) {
              props.showSaveBtn(false);
            }
          }

          if (event.empty) {
            setIsUsingExistingCard(false);
            // Clear name when switching to empty state
            setCardholderName('');
            // Setup mode specific logic
            if (isSetupMode) {
              props.refetchPaymentMethod();
            }
          }
        };

        paymentElement?.on('ready', handleReady);
        paymentElement?.on('change', handleChange);

        // Cleanup function to remove event listeners
        const cleanup = () => {
          paymentElement?.off('ready', handleReady);
          paymentElement?.off('change', handleChange);
        };

        return cleanup;
      }
      return undefined;
    }, [elements, isSetupMode, props]);

    useEffect(() => {
      if (paymentReady) {
        setIsStripeLoading(false);
      }
    }, [paymentReady]);

    const handleSubmit = async () => {
      if (!stripe || !elements) {
        return null;
      }

      // Validate cardholder name is required
      if (!cardholderName.trim()) {
        setNameError('Cardholder name is required');
        enqueueSnackbar(
          'Please enter a cardholder name',
          { variant: 'error' },
        );
        return null;
      }
      setNameError('');

      setIsStripeLoading(true);

      try {
        if (isSetupMode) {
        // Setup mode - create/update payment method
          const { paymentMethod } = await stripe.createPaymentMethod({
            elements,
          });

          const nameToUse = paymentMethod?.billing_details?.name || cardholderName;

          const { error } = await stripe.confirmSetup({
            elements,
            confirmParams: {
              return_url: window.location.href,
              payment_method_data: {
                allow_redisplay: 'always',
                billing_details: {
                  name: nameToUse,
                },
              },
            },
          });

          if (error) {
            enqueueSnackbar(
              error.message,
              { variant: 'error' },
            );
          }
        } else {
        // Payment mode - process payment
          const result = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
            confirmParams: {
              payment_method_data: {
                billing_details: {
                  name: cardholderName,
                },
              },
            },
          });

          if (result.error) {
            const errorMessage = result.error.message || 'Payment failed';
            props.onError?.(errorMessage);
          } else {
            props.onSuccess?.();
          }
        }
      } catch (error) {
        if (isSetupMode) {
        // Fallback for setup mode
          const { error: setupError } = await stripe.confirmSetup({
            elements,
            confirmParams: {
              return_url: window.location.href,
              payment_method_data: {
                allow_redisplay: 'always',
                billing_details: {
                  name: cardholderName,
                },
              },
            },
          });

          if (setupError) {
            enqueueSnackbar(
              setupError.message,
              { variant: 'error' },
            );
          }
        } else {
          props.onError?.('Payment processing failed');
        }
      }

      setIsStripeLoading(false);
      return null;
    };

    useImperativeHandle(ref, () => ({
      handleSubmit,
    }));

    return (
      <Box
        display="flex"
        height="100%"
        width="100%"
      >
        <form style={{ height: '100%', width: '100%' }}>
          {isStripeLoading && (<LoadingProgress fullscreen />)}
          <Box sx={{ mb: 2 }}>
            <InputLabel
              className={classes.cardHolderNameLabel}
              htmlFor="cardholder-name"
            >
              <FormattedMessage id="billinginfo.cardholderName" />
            </InputLabel>
            <TextField
              fullWidth
              id="cardholder-name"
              value={cardholderName}
              onChange={e => {
                setCardholderName(e.target.value);
                if (nameError) {
                  setNameError('');
                }
              }}
              placeholder="Enter cardholder name"
              disabled={isUsingExistingCard}
              required
              error={!!nameError}
              helperText={nameError || (isUsingExistingCard ? 'Name from saved payment method' : 'Enter the name on your card')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  fontSize: '16px',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#bdbdbd',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500,
                },
                '& .MuiOutlinedInput-input': {
                  '&::placeholder': {
                    color: '#9e9e9e',
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
          <PaymentElement
            id="stripe-payment-element"
            options={{
              layout: {
                type: 'tabs',
                defaultCollapsed: false,
              },
              wallets: {
                link: 'never', // THIS removes Email + Phone + Link UI
              },
            }}
          />
        </form>
      </Box>
    );
  });

export default PaymentForm;
